import React, { Suspense } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { VideoPlatformList } from "./VideoPlatformList";
import { MeetingDetailsDialog } from "./MeetingDetailsDialog";
import { DeleteMeetingDialog } from "./DeleteMeetingDialog";
import { CreateMeetingDialog } from "./CreateMeetingDialog";
import { useSuspense } from "@rest-hooks/react";
import { VideoPlatformResource } from "../../api/video_platform";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { MEETING_QUERY_PARAMS } from "./utils";
import { CreateMeetingFormData } from "./types";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";

const ManagementContent: React.FC = () => {
  const [selectedMeetingId, setSelectedMeetingId] = React.useState<
    string | null
  >(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [meetingToDelete, setMeetingToDelete] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const api = useVideoPlatformApi();
  const { withLoading } = useGlobalLoading();
  const { data: meetings = [] } = useSuspense(
    VideoPlatformResource.getMeetings,
    MEETING_QUERY_PARAMS
  );

  const selectedMeeting = React.useMemo(() => {
    if (!selectedMeetingId) return null;
    return meetings.find((m) => m.id === selectedMeetingId) || null;
  }, [selectedMeetingId, meetings]);

  const handleView = (meetingId: string) => {
    setSelectedMeetingId(meetingId);
    setDetailsDialogOpen(true);
  };

  const handleDelete = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setMeetingToDelete({ id: meeting.id, title: meeting.title });
      setDeleteDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setSelectedMeetingId(null);
    setDetailsDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!meetingToDelete) return;

    try {
      await withLoading(api.deleteMeeting(meetingToDelete.id));

      setSnackbar({
        open: true,
        message: "Meeting ended successfully",
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
      api.refreshMeetings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to end meeting";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMeetingToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    api
      .refreshMeetings()
      .then(() => {
        setSnackbar({
          open: true,
          message: "Meetings refreshed successfully",
          severity: "success",
        });
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refresh";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const handleStartNewMeeting = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateMeeting = async (formData: CreateMeetingFormData) => {
    try {
      await withLoading(api.createMeeting(formData));

      setSnackbar({
        open: true,
        message: "Meeting created successfully",
        severity: "success",
      });
      setCreateDialogOpen(false);
      api.refreshMeetings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create meeting";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <IconButton
          onClick={handleRefresh}
          disabled={isRefreshing}
          color="primary"
          sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
        >
          <RefreshIcon />
        </IconButton>
        <Button
          variant="contained"
          onClick={handleStartNewMeeting}
          sx={{
            backgroundColor: "#7357FF",
            "&:hover": { backgroundColor: "#5a42cc" },
          }}
        >
          Start a new meeting
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            localStorage.setItem("activeScreenId", "moderation");
            window.location.reload();
          }}
          sx={{
            borderColor: "#7357FF",
            color: "#7357FF",
            "&:hover": { borderColor: "#5a42cc", backgroundColor: "rgba(115, 87, 255, 0.04)" },
          }}
        >
          Moderation
        </Button>
      </Box>

      <VideoPlatformList onView={handleView} onDelete={handleDelete} />

      <MeetingDetailsDialog
        open={detailsDialogOpen}
        meeting={selectedMeeting}
        onClose={handleCloseDialog}
      />

      <DeleteMeetingDialog
        open={deleteDialogOpen}
        meetingTitle={meetingToDelete?.title || ""}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      <CreateMeetingDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateMeeting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export const Management: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Video Platform Meetings
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage video platform meetings
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Suspense
          fallback={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <ManagementContent />
        </Suspense>
      </Box>
    </Box>
  );
};
